import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { CalendarIcon, BuildingOfficeIcon, MapPinIcon, XMarkIcon } from '@heroicons/react/24/outline';
import type { Job } from '../../types/job';

interface JobCardProps {
  job: Job;
  onClick: () => void;
  onNotInterested: (id: number) => void;
}

// Separate JobList component to handle job rendering
const JobList = React.memo(({ 
  jobs, 
  onJobClick, 
  onNotInterested 
}: { 
  jobs: Job[], 
  onJobClick: (id: number) => void,
  onNotInterested: (id: number) => void
}) => {
  return (
    <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
      <AnimatePresence mode="popLayout">
        {jobs.map((job, index) => (
          <motion.div
            key={job.id}
            layout
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ 
              opacity: 1, 
              scale: 1,
              transition: { delay: index * 0.1 }
            }}
            exit={{ 
              opacity: 0, 
              scale: 0.9,
              transition: { duration: 0.2 }
            }}
            whileHover={{ y: -5 }}
            className="group relative bg-white/90 backdrop-blur-sm rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100/50 overflow-hidden h-[420px]"
          >
            <div 
              className="p-8 cursor-pointer h-full flex flex-col"
              onClick={() => onJobClick(job.id)}
            >
              <div className="flex-1 flex flex-col">
                {typeof job.relevance_score === 'number' && job.relevance_score > 0 && (
                  <span
                    className={`self-start inline-flex items-center gap-1 mb-2 px-2.5 py-1 rounded-full text-xs font-semibold ${
                      job.relevance_score >= 15
                        ? 'bg-green-100 text-green-700'
                        : 'bg-blue-50 text-blue-700'
                    }`}
                    title="Ranked using the preferences you set during onboarding"
                  >
                    {job.relevance_score >= 15 ? 'Strong match' : 'Match'}
                  </span>
                )}
                <motion.h2
                  className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors duration-300 line-clamp-2 mb-3"
                  layout="position"
                >
                  {job.title}
                </motion.h2>

                <div className="flex flex-wrap gap-3 mb-4">
                  <motion.div 
                    whileHover={{ scale: 1.05 }}
                    className="flex items-center text-gray-700 bg-blue-50 rounded-xl px-3 py-1.5"
                  >
                    <BuildingOfficeIcon className="h-4 w-4 mr-1.5 text-blue-600" />
                    <span className="text-sm font-medium truncate max-w-[120px]">{job.company}</span>
                  </motion.div>
                  <motion.div 
                    whileHover={{ scale: 1.05 }}
                    className="flex items-center text-gray-700 bg-blue-50 rounded-xl px-3 py-1.5"
                  >
                    <MapPinIcon className="h-4 w-4 mr-1.5 text-blue-600" />
                    <span className="text-sm font-medium truncate max-w-[120px]">{job.location}</span>
                  </motion.div>
                  {job.salary_min && job.salary_max && (
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      className="flex items-center text-gray-700 bg-green-50 rounded-xl px-3 py-1.5"
                    >
                      <span className="text-sm font-medium">{job.salary_min} - {job.salary_max} {job.salary_currency} {job.salary_period}</span>
                    </motion.div>
                  )}
                </div>

                <div className="relative flex-1">
                  <p className="text-sm text-gray-600 line-clamp-9 leading-relaxed mb-0 overflow-hidden" style={{display: '-webkit-box', WebkitLineClamp: '9', WebkitBoxOrient: 'vertical'}}>
                    {job.description?.replace(/<[^>]*>/g, '') || 'No description available'}
                  </p>
                  <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white to-transparent" />
                </div>

                <div className="flex items-center justify-between border-t border-gray-100 mt-1 pt-2">
                  <div className="flex items-center text-gray-500">
                    <CalendarIcon className="h-4 w-4 mr-1.5 text-blue-600" />
                    <span className="text-xs font-medium">
                      {new Date(job.timestamp).toLocaleDateString()}
                    </span>
                  </div>
                  <motion.span 
                    className="text-sm text-blue-600 font-bold flex items-center"
                    whileHover={{ x: 5 }}
                  >
                    View Details
                    <svg className="w-4 h-4 ml-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </motion.span>
                </div>
              </div>
            </div>
            
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={(e) => {
                e.stopPropagation();
                onNotInterested(job.id);
              }}
              className="absolute top-4 right-4 p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              title="Not Interested"
            >
              <XMarkIcon className="h-5 w-5" />
            </motion.button>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
});

export default JobList;
